/**
 * AdminInvitePage — public page for claiming an admin invite link.
 *
 * Flow:
 * 1. Extract token from ?token= URL param
 * 2. Validate token via GET /auth/admin-invite/{token}
 * 3. If not signed in → show sign-in/sign-up options with redirect preservation
 * 4. If signed in → show claim button
 * 5. After claim → show success + dashboard link
 */

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { ShieldCheck, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { getAdminInviteInfo, claimAdminInvite, fetchProfile } from '@/services/authApi';

type PageState = 'loading' | 'invalid' | 'valid' | 'claiming' | 'claimed';

export default function AdminInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';
  const { user } = useAuth();
  const { setUserProfile } = useWorkspaceStore();

  const [state, setState] = useState<PageState>('loading');
  const [role, setRole] = useState<string>('');
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!token) {
      setState('invalid');
      setError('No invite token provided.');
      return;
    }
    getAdminInviteInfo(token)
      .then((info) => {
        setRole(info.role);
        setExpiresAt(info.expires_at);
        setState('valid');
      })
      .catch((err: unknown) => {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 410) {
          setError('This invite link has expired.');
        } else {
          setError('This invite link is invalid or has already been used.');
        }
        setState('invalid');
      });
  }, [token]);

  async function handleClaim() {
    setState('claiming');
    try {
      const result = await claimAdminInvite(token);
      // Refresh profile in store so nav updates immediately
      const profile = await fetchProfile();
      setUserProfile(profile);
      setRole(result.platform_role);
      setState('claimed');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to claim invite';
      setError(msg);
      setState('invalid');
    }
  }

  const redirectUrl = `/auth/admin-invite?token=${encodeURIComponent(token)}`;
  const roleLabel = role === 'super_admin' ? 'Super Admin' : role;

  return (
    <div className="min-h-screen bg-warm-bg flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo / branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-xl bg-teal-light mb-3">
            <ShieldCheck className="h-6 w-6 text-teal-dark" />
          </div>
          <h1 className="text-xl font-semibold">Admin Invite</h1>
        </div>

        {/* Loading */}
        {state === 'loading' && (
          <Card>
            <CardContent className="py-10 flex justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-teal border-t-transparent" />
            </CardContent>
          </Card>
        )}

        {/* Invalid / expired */}
        {state === 'invalid' && (
          <Card>
            <CardHeader className="text-center">
              <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-2" />
              <CardTitle>Invite Unavailable</CardTitle>
              <CardDescription>{error || 'This invite link is invalid or has expired.'}</CardDescription>
            </CardHeader>
          </Card>
        )}

        {/* Valid — not signed in */}
        {state === 'valid' && !user && (
          <Card>
            <CardHeader className="text-center">
              <CardTitle>You've been invited</CardTitle>
              <CardDescription>
                Sign in or create an account to accept this invite.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-md bg-teal-light px-4 py-3 flex items-center justify-between">
                <span className="text-sm text-teal-dark font-medium">Invited role</span>
                <Badge variant="outline" className="bg-card text-teal-dark border-teal/20">{roleLabel}</Badge>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                Expires {new Date(expiresAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
              <div className="space-y-2">
                <Button asChild className="w-full bg-teal hover:bg-teal-hover text-white">
                  <Link to={`/login?redirect=${encodeURIComponent(redirectUrl)}`}>
                    Sign In to Accept
                  </Link>
                </Button>
                <Button asChild variant="outline" className="w-full">
                  <Link to={`/signup?redirect=${encodeURIComponent(redirectUrl)}`}>
                    Create Account
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Valid — signed in, ready to claim */}
        {state === 'valid' && user && (
          <Card>
            <CardHeader className="text-center">
              <CardTitle>Accept Admin Invite</CardTitle>
              <CardDescription>
                You've been invited to join as a platform admin.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-md bg-teal-light px-4 py-3 flex items-center justify-between">
                <span className="text-sm text-teal-dark font-medium">Your new role</span>
                <Badge variant="outline" className="bg-card text-teal-dark border-teal/20">{roleLabel}</Badge>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                Expires {new Date(expiresAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
              <Button onClick={handleClaim} className="w-full bg-teal hover:bg-teal-hover text-white">
                Accept &amp; Become {roleLabel}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Claiming */}
        {state === 'claiming' && (
          <Card>
            <CardContent className="py-10 flex flex-col items-center gap-3">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-teal border-t-transparent" />
              <p className="text-sm text-muted-foreground">Applying your role...</p>
            </CardContent>
          </Card>
        )}

        {/* Claimed */}
        {state === 'claimed' && (
          <Card>
            <CardHeader className="text-center">
              <CheckCircle2 className="h-10 w-10 text-teal mx-auto mb-2" />
              <CardTitle>You are now a {roleLabel}</CardTitle>
              <CardDescription>
                Your platform role has been updated. You now have admin access.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate('/')} className="w-full bg-teal hover:bg-teal-hover text-white">
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
